import type { BrandConnectionSyncDecision } from './brandConnectionSync';

export type ConnectionBrandAssignmentMode = 'inline' | 'after_authorization';

export type BusinessGroupAvailability = 'loading' | 'available' | 'only_default' | 'empty';

export type ConnectionBrandAssignmentAction =
    | { kind: 'wait' }
    | { kind: 'prompt'; connectionKeys: string[] }
    | { kind: 'assign'; connectionKeys: string[]; brandIds: string[] }
    | { kind: 'complete' };

export type DefaultBusinessGroupOption = {
    id: string;
    /** Spring may serialize the Java isDefault accessor as `default`. */
    default?: boolean;
    isDefault?: boolean;
};

export type BusinessGroupAssignmentAccessLevels = Record<string, 'READ' | 'WRITE'>;

export const getDefaultBusinessGroupId = (
    businessGroups: readonly DefaultBusinessGroupOption[],
): string | null => businessGroups.find(
    (businessGroup) => businessGroup.isDefault === true || businessGroup.default === true,
)?.id || null;

/**
 * The connection service always retains the Workspace default Business group.
 * Keep that invariant visible in every post-connection assignment surface so
 * choosing no additional groups never looks like an unassigned connection.
 */
export const createDefaultBusinessGroupAssignmentDraft = (
    businessGroups: readonly DefaultBusinessGroupOption[],
    selectedBrandIds: readonly string[] = [],
    accessLevels: Readonly<BusinessGroupAssignmentAccessLevels> = {},
): {
    defaultBusinessGroupId: string | null;
    selectedBrandIds: string[];
    accessLevels: BusinessGroupAssignmentAccessLevels;
} => {
    const defaultBusinessGroupId = getDefaultBusinessGroupId(businessGroups);
    const nextSelectedBrandIds = Array.from(new Set([
        ...selectedBrandIds,
        ...(defaultBusinessGroupId ? [defaultBusinessGroupId] : []),
    ]));

    return {
        defaultBusinessGroupId,
        selectedBrandIds: nextSelectedBrandIds,
        accessLevels: Object.fromEntries(
            nextSelectedBrandIds.map((brandId) => [
                brandId,
                accessLevels[brandId] || 'WRITE',
            ]),
        ),
    };
};

export const resolveOnlyDefaultBusinessGroupId = (
    businessGroups: readonly DefaultBusinessGroupOption[],
): string | null => {
    const defaultBusinessGroupId = getDefaultBusinessGroupId(businessGroups);
    return businessGroups.length === 1 ? defaultBusinessGroupId : null;
};

interface ResolveConnectionBrandAssignmentActionInput {
    mode: ConnectionBrandAssignmentMode;
    syncDecision: BrandConnectionSyncDecision;
    selectedBrandIds: string[];
    promptAlreadyOpen: boolean;
    businessGroupAvailability?: BusinessGroupAvailability;
    autoAssignBusinessGroupId?: string | null;
}

export const resolveConnectionBrandAssignmentAction = ({
    mode,
    syncDecision,
    selectedBrandIds,
    promptAlreadyOpen,
    businessGroupAvailability = 'available',
    autoAssignBusinessGroupId = null,
}: ResolveConnectionBrandAssignmentActionInput): ConnectionBrandAssignmentAction => {
    if (syncDecision.kind !== 'ready') {
        return { kind: 'wait' };
    }

    if (mode === 'after_authorization') {
        if (businessGroupAvailability === 'loading') {
            return { kind: 'wait' };
        }

        if (businessGroupAvailability === 'empty') {
            return { kind: 'complete' };
        }

        // A sole default Business group leaves no assignment decision for the
        // user to make. When supplied, persist that required association with
        // the same READ ceiling used by every explicit assignment; otherwise
        // the connection service remains the compatibility fallback.
        if (businessGroupAvailability === 'only_default') {
            if (autoAssignBusinessGroupId) {
                return {
                    kind: 'assign',
                    connectionKeys: syncDecision.connectionKeys,
                    brandIds: [autoAssignBusinessGroupId],
                };
            }
            return { kind: 'complete' };
        }

        return promptAlreadyOpen
            ? { kind: 'wait' }
            : { kind: 'prompt', connectionKeys: syncDecision.connectionKeys };
    }

    if (selectedBrandIds.length > 0) {
        return {
            kind: 'assign',
            connectionKeys: syncDecision.connectionKeys,
            brandIds: selectedBrandIds,
        };
    }

    return { kind: 'complete' };
};
